import {
  FOCUS_TEXT_CONTEXT_MEDIA_TYPES,
  type CapabilityManifest,
  type DocumentList,
  type FocusMessageProjection,
  type TaskScopedList,
} from "@mind/contracts/browser";

export type ContextChoice = CapabilityManifest["contextItems"][number] & {
  readonly key: string;
  readonly label: string;
  readonly detail: string | null;
  readonly disabledReason: string | null;
};

export type ContextDocumentReasons = {
  readonly uploading: string;
  readonly security: string;
  readonly unsafe: string;
  readonly unsupported: string;
  readonly blocked: string;
};

export function contextDocumentReason(document: DocumentList["items"][number], reasons: ContextDocumentReasons): string | null {
  if (document.ingestState === "uploading") return reasons.uploading;
  if (document.ingestState === "rejected_unsafe") return reasons.unsafe;
  if (document.ingestState !== "ready" || document.currentSha256 === null) return reasons.security;
  if (document.aiCapability === "blocked") return reasons.blocked;
  if (
    !document.surfaceCapabilities.focusContext
    || !FOCUS_TEXT_CONTEXT_MEDIA_TYPES.some((mediaType) => mediaType === document.mediaType)
  ) return reasons.unsupported;
  return null;
}

export function buildContextChoices(
  documents: DocumentList["items"],
  tasks: TaskScopedList["items"],
  messages: readonly FocusMessageProjection[],
  messageLabel: string,
  reasons: ContextDocumentReasons,
): readonly ContextChoice[] {
  const choices: ContextChoice[] = [];
  let documentCount = 0;
  for (const document of documents) {
    const disabledReason = contextDocumentReason(document, reasons);
    choices.push({
      key: `document:${document.id}`,
      kind: "document",
      id: document.id,
      revision: document.revision,
      versionSha256: document.currentSha256,
      ranges: [],
      label: document.displayName,
      detail: null,
      disabledReason,
    });
    documentCount += 1;
    if (documentCount >= 30) break;
  }
  for (const task of tasks.slice(0, 30)) {
    choices.push({
      key: `task:${task.id}`,
      kind: "task",
      id: task.id,
      revision: task.revision,
      versionSha256: null,
      ranges: [],
      label: task.title,
      detail: null,
      disabledReason: null,
    });
  }
  for (const message of messages.slice(-20)) {
    choices.push({
      key: `focus_message:${message.id}`,
      kind: "focus_message",
      id: message.id,
      revision: message.sequence,
      versionSha256: null,
      ranges: [],
      label: message.bodyMarkdown.slice(0, 120),
      detail: messageLabel,
      disabledReason: null,
    });
  }
  return choices;
}
